'use client';

import * as React from 'react';
import { memo, useRef, useCallback, startTransition } from 'react';
import { cn } from '@/lib/utils';
import { ProductsGrid } from './products-grid';
import { ProductCard } from './product-card';
import { ProductSkeleton } from './product-skeleton';
import { ViewToggle } from './view-toggle';
import type { ProductWithCategories } from '@/lib/api/products';

export interface CategorySectionProps {
  id: string;
  title: string;
  description?: string;
  products: ProductWithCategories[];
  isLoading?: boolean;
  getQuantity?: (productId: number) => number;
  onAddToCart?: (product: ProductWithCategories) => void;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onOpenPopup?: (product: ProductWithCategories) => void;
  className?: string;
  columns?: 1 | 2 | 3 | 4 | 5;
  /** Показывать горизонтальный скролл на мобильных */
  mobileHorizontal?: boolean;
  /** Первая секция - для приоритетной загрузки изображений */
  isFirstSection?: boolean;
  /** Принудительный режим мобильной сетки (переопределяет mobileHorizontal) */
  mobileGridMode?: boolean;
  /** Показать переключатель вида в заголовке */
  showViewToggle?: boolean;
}

// Мемоизированная карточка для горизонтального скролла
const HorizontalProductCard = memo<{
  product: ProductWithCategories;
  quantity: number;
  onAddToCart?: (product: ProductWithCategories) => void;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onOpenPopup?: (product: ProductWithCategories) => void;
}>(({ product, quantity, onAddToCart, onQuantityChange, onOpenPopup }) => {
  const handleAddToCart = useCallback(() => {
    if (onAddToCart) {
      startTransition(() => {
        onAddToCart(product);
      });
    }
  }, [onAddToCart, product]);
  
  const handleOpenPopup = useCallback(() => {
    if (onOpenPopup) {
      startTransition(() => {
        onOpenPopup(product);
      });
    }
  }, [onOpenPopup, product]);
  
  return (
    <div className="w-[150px] flex-shrink-0">
      <ProductCard
        product={{
          id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          spiciness: product.spiciness,
          isNew: product.is_new,
          isPopular: product.is_popular,
          isRiceless: product.is_riceless,
          isScorched: product.is_scorched,
          isMini: product.is_mini,
          isAvailable: product.status === 'available',
        }}
        quantity={quantity}
        onAddToCart={handleAddToCart}
        onQuantityChange={onQuantityChange}
        onOpenPopup={handleOpenPopup}
      />
    </div>
  );
});
HorizontalProductCard.displayName = 'HorizontalProductCard';

export function CategorySection({
  id,
  title,
  description,
  products,
  isLoading = false,
  getQuantity,
  onAddToCart,
  onQuantityChange,
  onOpenPopup,
  className,
  columns = 4,
  mobileHorizontal = true,
  isFirstSection = false,
  mobileGridMode = false,
  showViewToggle = false,
}: CategorySectionProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Если mobileGridMode — показываем сетку 2x2 на мобилке вместо горизонтального скролла
  const showHorizontal = mobileHorizontal && !mobileGridMode;

  return (
    <section
      id={id}
      // pb-4 здесь — не отступ, а МЕСТО ПОД ТЕНЬ.
      // Секция каталога помечена классом catalog-section, а он в globals.css
      // включает content-visibility: auto. Это не только пропуск отрисовки
      // за экраном: заодно включается paint-изоляция, то есть всё, что дети
      // рисуют дальше внутреннего края секции, обрезается. Тень карточки
      // уходит вниз на 16px (смещение 4 + размытие 12), поэтому у НИЖНЕГО
      // ряда она срезалась ровно по краю секции. Сверху обрезки нет: над
      // первым рядом внутри секции лежит её заголовок, места хватает.
      // Вертикальный ритм не изменился: ровно на эти 16px уменьшен зазор
      // между секциями в catalog.tsx (было 48 зазора, стало 32 + 16 здесь).
      className={cn('scroll-mt-24 catalog-section pb-4', className)}
    >
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-center justify-between gap-3 mr-4 sm:mr-0">
        <div>
          <h2 className="text-lg md:text-2xl font-heading font-bold text-[var(--text-primary)]">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm md:text-base text-[var(--text-secondary)]">{description}</p>
          )}
        </div>
        {showViewToggle && (
          <div className="flex items-center gap-1.5">
            <ViewToggle />
          </div>
        )}
      </div>

      {/* Mobile: Горизонтальный скролл (только если не grid mode) */}
      {showHorizontal && (
        <div 
          className="md:hidden"
        >
          <div
            ref={scrollContainerRef}
            className={cn(
              'flex gap-3 overflow-x-auto',
              'hide-scrollbar'
            )}
          >
            {isLoading ? (
              // Скелетоны для загрузки
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="w-[150px] flex-shrink-0">
                  <ProductSkeleton />
                </div>
              ))
            ) : (
              products.map((product) => (
                <HorizontalProductCard
                  key={product.id}
                  product={product}
                  quantity={getQuantity?.(product.id) || 0}
                  onAddToCart={onAddToCart}
                  onQuantityChange={onQuantityChange}
                  onOpenPopup={onOpenPopup}
                />
              ))
            )}
            {/* Псевдо-отступ справа */}
            <div className="w-4 sm:w-6 flex-shrink-0" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* Desktop: Сетка товаров (+ Mobile grid mode) */}
      <div className={cn(showHorizontal ? 'hidden md:block' : 'mr-4 sm:mr-0', 'md:mr-0')}>
        <ProductsGrid
          products={products}
          isLoading={isLoading}
          getQuantity={getQuantity}
          onAddToCart={onAddToCart}
          onQuantityChange={onQuantityChange}
          onOpenPopup={onOpenPopup}
          columns={columns}
          isFirstSection={isFirstSection}
        />
      </div>
    </section>
  );
}

/**
 * Navigation for category sections
 */
export interface CategoryNavItem {
  id: string;
  name: string;
  count?: number;
}

interface CategoryNavProps {
  categories: CategoryNavItem[];
  activeId?: string;
  onSelect?: (id: string) => void;
  className?: string;
}

export function CategoryNav({
  categories,
  activeId,
  onSelect,
  className,
}: CategoryNavProps) {
  const handleClick = useCallback((id: string) => {
    onSelect?.(id);
    
    // Smooth scroll to section
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [onSelect]);

  return (
    <nav
      className={cn(
        'flex gap-2 overflow-x-auto pb-2 hide-scrollbar',
        className
      )}
    >
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => handleClick(category.id)}
          className={cn(
            // Чип — капсула (pill), а не карточка: так записано в системе.
            'flex-shrink-0 px-4 py-2 rounded-pill',
            // Роль интерактивной подписи (14/600). Заведена ровно из-за
            // этого места: на обычной роли основного текста (14/400) подпись
            // активного чипа читалась как рядовой абзац.
            't-label whitespace-nowrap',
            'transition-all duration-200',
            'border',
            activeId === category.id
              ? [
                  'bg-[var(--primary)] text-white',
                  'border-[var(--primary)]',
                  // Фирменный ореол сохранён (ответ 35 = Б), но через роль
                  // «свечение»: она сама меняет силу в тёмной теме.
                  'shadow-glow',
                ]
              : [
                  'bg-[var(--surface)] text-[var(--text-secondary)]',
                  'border-[var(--border-default)]',
                  'hover:border-[var(--primary)]/30 hover:text-[var(--text-primary)]',
                ]
          )}
        >
          {category.name}
          {category.count !== undefined && (
            <span
              className={cn(
                'ml-1.5 t-caption',
                activeId === category.id
                  ? 'text-white/70'
                  : 'text-[var(--text-tertiary)]'
              )}
            >
              ({category.count})
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}

